import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    MenuItem,
    Pagination,
    Skeleton,
    IconButton,
    Breadcrumbs,
    Link,
    Tooltip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AdminGuard from './AdminGuard'
import { useAdminAuditLogsQuery, AuditLog, AuditLogsFilters } from '../../shared/queries/admin'

const ACTION_LABELS: Record<string, string> = {
    VIEW_ORGANIZATIONS: 'View Organizations',
    VIEW_ORGANIZATION_DETAIL: 'View Org Detail',
    UPDATE_ORG_FLAGS: 'Update Org Flags',
    VIEW_ORG_USERS: 'View Org Users',
    UPDATE_USER_ROLES: 'Update User Roles',
    VIEW_ORG_ROADMAPS: 'View Org Roadmaps',
    VIEW_ROADMAP_ALLOWED_USERS: 'View Roadmap Access',
    UPDATE_ROADMAP_ACCESS: 'Update Roadmap Access',
}

const ACTION_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'error'> = {
    VIEW_ORGANIZATIONS: 'default',
    VIEW_ORGANIZATION_DETAIL: 'default',
    UPDATE_ORG_FLAGS: 'warning',
    VIEW_ORG_USERS: 'default',
    UPDATE_USER_ROLES: 'warning',
    VIEW_ORG_ROADMAPS: 'default',
    VIEW_ROADMAP_ALLOWED_USERS: 'default',
    UPDATE_ROADMAP_ACCESS: 'warning',
}

const AdminAuditLogs: React.FC = () => {
    const history = useHistory()
    const [page, setPage] = useState(1)
    const [filters, setFilters] = useState<AuditLogsFilters>({
        page: 1,
        limit: 50,
    })

    const { data, isLoading } = useAdminAuditLogsQuery({ ...filters, page })

    const logs = data?.data?.logs || []
    const pagination = data?.data?.pagination

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString(undefined, { timeZoneName: 'short' })
    }

    const handleActionFilterChange = (action: string) => {
        setFilters((prev) => ({ ...prev, action: action || undefined }))
        setPage(1)
    }

    return (
        <AdminGuard>
            <Box sx={{ p: 3 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link
                        component="button"
                        variant="body1"
                        onClick={() => history.push('/admin')}
                        sx={{ cursor: 'pointer' }}
                    >
                        Admin Panel
                    </Link>
                    <Typography color="text.primary">Audit Logs</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <IconButton onClick={() => history.push('/admin')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        Audit Logs
                    </Typography>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <TextField
                        select
                        size="small"
                        label="Action Type"
                        value={filters.action || ''}
                        onChange={(e) => handleActionFilterChange(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">All Actions</MenuItem>
                        {Object.entries(ACTION_LABELS).map(([key, label]) => (
                            <MenuItem key={key} value={key}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Admin</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell>
                                              <Skeleton />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : logs.map((log: AuditLog) => (
                                      <TableRow key={log._id}>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                              {formatDate(log.createdAt)}
                                          </TableCell>
                                          <TableCell>
                                              <Tooltip title={log.adminEmail}>
                                                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                      {log.adminUserId?.name || log.adminEmail}
                                                  </Typography>
                                              </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                              <Chip
                                                  label={ACTION_LABELS[log.action] || log.action}
                                                  size="small"
                                                  color={ACTION_COLORS[log.action] || 'default'}
                                              />
                                          </TableCell>
                                          <TableCell>{log.targetOrganizationId?.name || '-'}</TableCell>
                                          <TableCell>
                                              {log.newValues && Object.keys(log.newValues).length > 0 && (
                                                  <Tooltip
                                                      title={
                                                          <pre style={{ margin: 0, fontSize: 11 }}>
                                                              {JSON.stringify(log.newValues, null, 2)}
                                                          </pre>
                                                      }
                                                  >
                                                      <Chip
                                                          label="View Details"
                                                          size="small"
                                                          variant="outlined"
                                                          sx={{ cursor: 'pointer' }}
                                                      />
                                                  </Tooltip>
                                              )}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {pagination && pagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}

                {!isLoading && logs.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No audit logs found</Typography>
                    </Box>
                )}
            </Box>
        </AdminGuard>
    )
}

export default AdminAuditLogs
